/* @HEADER
 * @version 1.0.1291 | 2026-09-12
 * @purpose Asks, before a Space is deleted, what it holds and where its boards should go.
 * @sidecar SpaceDeleteDialog.tsx.header.md
 * @layer app | @hex adapter | @ctx kanban
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-5637, ZVX-DEV-5636, ZVX-DEV-2692, ZVX-DEV-3615, ZVX-DEV-5813

/**
 * Modelled on `ProjectOrgConfirmDialog` — portal render, `role="alertdialog"`,
 * two-button focus trap, Esc and overlay cancel, focus returned on close. That
 * component's own header records the same lineage from `OkrConfirmDialog`, and
 * this is the fourth in the line rather than a new pattern.
 *
 * What it adds is the reason it cannot BE one of them: those answer yes-or-no,
 * and this one has to carry a choice. Deleting a board already asks where its
 * tasks go (`BoardSettingsPanel`); deleting a task with subtasks asks
 * detach-or-cascade. The rule — when the target has dependents, the decision
 * goes to the person — simply never reached the Space.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { BOARD_NAV_TESTIDS } from './board-nav.testids'

import './SpaceDeleteDialog.css'

export interface SpaceDeleteDialogSpace {
  id: string
  name: string
}

export interface SpaceDeleteDialogMessages {
  title: (spaceName: string) => string
  boardCount: (count: number) => string
  empty: string
  moveTo: string
  confirm: string
  cancel: string
}

export interface SpaceDeleteDialogProps {
  open: boolean
  spaceName: string
  /** How many boards the Space holds — 0 makes this a plain confirmation. */
  boardCount: number
  /** Spaces that may receive the boards; empty means no question is put. */
  destinations: readonly SpaceDeleteDialogSpace[]
  messages: SpaceDeleteDialogMessages
  /** `destinationGroupId` is undefined when nothing was asked or nothing chosen. */
  onConfirm: (destinationGroupId?: string) => void
  onCancel: () => void
}

export function SpaceDeleteDialog({
  open,
  spaceName,
  boardCount,
  destinations,
  messages,
  onConfirm,
  onCancel,
}: SpaceDeleteDialogProps) {
  const titleId = useId()
  const bodyId = useId()
  const cancelRef = useRef<HTMLButtonElement | null>(null)
  const returnFocusTo = useRef<Element | null>(null)

  const asksWhere = boardCount > 0 && destinations.length > 0
  const [destination, setDestination] = useState<string>('')

  // The first destination is preselected, so confirming without touching the
  // picker still puts the boards somewhere the person can see rather than
  // falling through to the catch-all they complained about.
  useEffect(() => {
    if (open && asksWhere) setDestination(destinations[0]?.id ?? '')
  }, [open, asksWhere, destinations])

  useEffect(() => {
    if (!open) return
    returnFocusTo.current = document.activeElement
    cancelRef.current?.focus()
    return () => {
      const back = returnFocusTo.current
      if (back instanceof HTMLElement) back.focus()
    }
  }, [open])

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onCancel()
      }
    },
    [onCancel],
  )

  if (!open) return null

  return createPortal(
    <div
      className="space-delete-dialog__overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel() }}
      onKeyDown={onKeyDown}
    >
      <div
        className="space-delete-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        data-testid={BOARD_NAV_TESTIDS.deleteSpaceDialog}
      >
        <h2 className="space-delete-dialog__title" id={titleId}>
          {messages.title(spaceName)}
        </h2>

        <p
          className="space-delete-dialog__body"
          id={bodyId}
          data-testid={BOARD_NAV_TESTIDS.deleteSpaceBoardCount}
        >
          {boardCount > 0 ? messages.boardCount(boardCount) : messages.empty}
        </p>

        {asksWhere && (
          <label className="space-delete-dialog__move">
            {messages.moveTo}
            <select
              className="space-delete-dialog__select"
              data-testid={BOARD_NAV_TESTIDS.deleteSpaceDestination}
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            >
              {destinations.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
        )}

        <div className="space-delete-dialog__actions">
          <button
            type="button"
            ref={cancelRef}
            className="space-delete-dialog__cancel"
            data-testid={BOARD_NAV_TESTIDS.deleteSpaceCancelBtn}
            onClick={onCancel}
          >
            {messages.cancel}
          </button>
          <button
            type="button"
            className="space-delete-dialog__confirm"
            data-testid={BOARD_NAV_TESTIDS.deleteSpaceConfirmBtn}
            onClick={() => onConfirm(asksWhere ? destination || undefined : undefined)}
          >
            {messages.confirm}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
